// React
import React, { useEffect, useRef, useMemo, useState } from "react";

// D3
import * as d3 from "d3";

// Hooks
import useResponsiveState from "../../../../hooks/useResponsiveState";

// Styles
import "./graph.css";

// --- HELPER: Linear Regression Calculation ---
const calculateRegression = (data) => {
  const n = data.length;
  if (n < 2) return null;

  const sumX = d3.sum(data, (d) => d.x);
  const sumY = d3.sum(data, (d) => d.y);
  const sumXY = d3.sum(data, (d) => d.x * d.y);
  const sumXX = d3.sum(data, (d) => d.x * d.x);

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
};

// --- HELPER: Fit an axis to the data it is showing ---
// The declared range says what a reading *can* be (feeling 0-5, doing 0-50),
// not what a given reader's days look like. Someone whose days all land between
// 28 and 41 got a cloud squeezed into the middle third of a 0-50 axis, markers
// piled on each other with very little to aim at. Fitting the axis spreads the
// same points across the whole tile. The declared range stays the hard cap: a
// feeling axis never runs past 5, a doing axis never below 0.
const fitDomain = (values, declared) => {
  const lo = d3.min(values);
  const hi = d3.max(values);
  if (lo === undefined || hi === undefined) return declared;

  const [floor, ceil] = declared;
  const span = hi - lo;
  // The padding keeps the extreme markers off the frame, where half the circle
  // falls outside the plot and is that much harder to click. One distinct value
  // has no span to work from, so it borrows a slice of the declared range.
  const pad = span > 0 ? span * 0.08 : Math.max((ceil - floor) * 0.04, 0.5);

  let min = lo - pad;
  let max = hi + pad;

  // A reader with a perfect day pushes the padding past the top of the scale,
  // and a feeling axis running to 5.4 is not a thing. Clamp there, but hand the
  // room back at the other end so the fitted window keeps its breathing space.
  if (min < floor) {
    max = Math.min(ceil, max + (floor - min));
    min = floor;
  }
  if (max > ceil) {
    min = Math.max(floor, min - (max - ceil));
    max = ceil;
  }

  return [min, max];
};

// Ticks a reader recognises. Asked for five over a short fitted span, d3 answers
// in halves - a "how you felt" axis reading 2, 2.5, 3 invents a precision the
// 0-5 score does not have - so whole-numbered data gets whole-numbered ticks.
const axisTicks = (domain, values, count) => {
  const [lo, hi] = domain;
  if (values.every((v) => Number.isInteger(v)) && hi - lo <= count + 1) {
    return d3.range(Math.ceil(lo), Math.floor(hi) + 1);
  }
  return d3.ticks(lo, hi, count);
};

export default function RegressionChart({
  config,
  aggregate,
  onNodeSelect = () => {},
  onNodeHover = () => {},
  selectedId = null,
}) {
  const chartRef = useRef(null);
  const { viewType } = useResponsiveState();

  // The tile is fluid now, so redraw when its box changes.
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = chartRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setChartSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // --- 1. PREPARE DATA ---
  const processedData = useMemo(() => {
    // Safety check for config
    if (!config || !config.graphData) return [];

    return config.graphData
      .map((d) => ({
        ...d,
        x: +d.x,
        y: +d.y,
      }))
      .filter((d) => !isNaN(d.x) && !isNaN(d.y));
  }, [config]);

  // --- 2. RENDER D3 ---
  useEffect(() => {
    if (!chartRef.current) return;

    const chartContainer = d3.select(chartRef.current);
    chartContainer.select("svg").remove();

    // Layout Dimensions - Safety fallback included
    const margin = (viewType === "mobile"
      ? config?.margin?.mobile
      : config?.margin?.desktop) || {
      top: 20,
      right: 30,
      bottom: 40,
      left: 40,
    };

    const width =
      chartContainer.node().getBoundingClientRect().width -
      margin.left -
      margin.right;
    const height =
      chartContainer.node().getBoundingClientRect().height -
      margin.top -
      margin.bottom;

    const svg = chartContainer
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // --- SCALES (With Safety Checks) ---
    // The declared range is still the measure's own scale: ChartHeader reads it
    // for the "/5" denominator, and the endpoint labels below are named from it.
    const declaredX = config?.xRange || [0, 50];
    const declaredY = config?.yRange || [0, 5];

    // Fitting is opt-in per graph, via the flags in graphconfig, so a chart that
    // needs the frame it declares keeps it.
    const fitted = config?.fitAxesToData && processedData.length > 0;
    const xValues = processedData.map((d) => d.x);
    const yValues = processedData.map((d) => d.y);

    // sharedAxisScale fits ONE domain across both axes. Where the two axes carry
    // the same measure, the graph is read against the y = x diagonal - "above
    // the line means the day got better" - and that reading only survives while
    // a step along x is the same size as a step along y. Fitting the two axes
    // separately would slide the diagonal off the points it describes.
    const shared = fitted && config?.sharedAxisScale;
    const sharedDomain = shared
      ? fitDomain(xValues.concat(yValues), [
          Math.min(declaredX[0], declaredY[0]),
          Math.max(declaredX[1], declaredY[1]),
        ])
      : null;

    let xDomain = declaredX;
    let yDomain = declaredY;
    if (shared) {
      xDomain = sharedDomain;
      yDomain = sharedDomain;
    } else if (fitted) {
      xDomain = fitDomain(xValues, declaredX);
      yDomain = fitDomain(yValues, declaredY);
    }

    const x = d3.scaleLinear().domain(xDomain).range([0, width]);
    const y = d3.scaleLinear().domain(yDomain).range([height, 0]);

    // --- AXIS LABELS ---
    // Updated Helper: Now safer against undefined ranges or labels
    const formatLabel = (val, labels, range) => {
      if (!range || !labels) return val; // Prevent crash if config is missing
      if (val === range[0] && labels[0]) return labels[0];
      if (val === range[1] && labels[1]) return labels[1];
      return val;
    };

    // Each axis and the gridlines share one set of tick values, so a fitted
    // domain cannot end up with gridlines that miss its labels. On a shared
    // scale both axes read off the same values too, or the symmetry the diagonal
    // depends on would only be true of the domain and not of the labels.
    const tickSource = shared ? xValues.concat(yValues) : null;
    const xTicks = axisTicks(xDomain, tickSource || xValues, 5);
    const yTicks = axisTicks(yDomain, tickSource || yValues, 5);

    // X Axis
    const xAxis = d3
      .axisBottom(x)
      .tickValues(xTicks)
      .tickFormat((d) => formatLabel(d, config?.xAxisLabels, declaredX));

    svg
      .append("g")
      .attr("class", "axis x-axis")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis)
      .select(".domain")
      .remove();

    // Y Axis
    const yAxis = d3
      .axisLeft(y)
      .tickValues(yTicks)
      .tickFormat((d) => formatLabel(d, config?.yAxisLabels, declaredY));

    svg
      .append("g")
      .attr("class", "axis y-axis")
      .call(yAxis)
      .call((g) => g.selectAll(".tick line").attr("stroke-opacity", 0.3))
      .call((g) => g.select(".domain").remove());

    // AXIS TITLES
    svg
      .append("text")
      .attr("class", "y-axis-title")
      .attr("transform", "rotate(-90)")
      .attr("x", 0 - height / 2)
      .attr("y", 0 - margin.left + 12)
      .attr("text-anchor", "middle")
      .style("fill", "#e2dfcf")
      .style("font-size", "0.7rem")
      .style("opacity", 0.7)
      .text(config?.yAxisTitle || "");

    svg
      .append("text")
      .attr("class", "x-axis-title")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 6)
      .attr("text-anchor", "middle")
      .style("fill", "#e2dfcf")
      .style("font-size", "0.7rem")
      .style("opacity", 0.7)
      .text(config?.xAxisTitle || "");

    const makeXGridlines = () => d3.axisBottom(x).tickValues(xTicks);

    // Add X Gridlines (Vertical lines)
    svg
      .append("g")
      .attr("class", "grid grid-vertical")
      .attr("transform", `translate(0,${height})`)
      .call(
        makeXGridlines()
          .tickSize(-height) // This stretches the tick line across the chart
          .tickFormat("") // We don't want extra labels here
      );

    // PLOT POINTS
    svg
      .selectAll("circle")
      .data(processedData)
      .enter()
      .append("circle")
      .attr("class", (d) =>
        selectedId != null && d.id === selectedId
          ? "data-node data-node-selected"
          : "data-node data-node-default",
      )
      .attr("cx", (d) => x(d.x))
      .attr("cy", (d) => y(d.y))
      .attr("r", 5)
      .style("cursor", "pointer")
      // Hover feeds the day record panel on the right, which replaced the
      // tooltip. The class toggle stays imperative so React never re-renders
      // mid-hover and force a redraw.
      .on("mouseenter", (event, d) => {
        svg.selectAll(".data-node").classed("data-node-hovered", false);
        d3.select(event.currentTarget)
          .classed("data-node-hovered", true)
          .raise();
        onNodeHover(d);
      })
      .on("mouseleave", (event) => {
        d3.select(event.currentTarget).classed("data-node-hovered", false);
        onNodeHover(null);
      })
      .on("click", (event, d) => {
        event.stopPropagation();
        onNodeSelect(d);
      });

    // REGRESSION LINE
    const regression = calculateRegression(processedData);
    if (regression) {
      const { slope, intercept } = regression;

      // Clipped to the plotted box. Across a fitted y-axis the trendline can
      // leave through the top or the bottom, and the plot carries no clip path -
      // an unclipped line would draw straight over the axis titles.
      let x1 = xDomain[0];
      let x2 = xDomain[1];
      let visible = true;

      if (slope === 0) {
        visible = intercept >= yDomain[0] && intercept <= yDomain[1];
      } else {
        const atFloor = (yDomain[0] - intercept) / slope;
        const atCeil = (yDomain[1] - intercept) / slope;
        x1 = Math.max(x1, Math.min(atFloor, atCeil));
        x2 = Math.min(x2, Math.max(atFloor, atCeil));
        visible = x2 > x1;
      }

      if (visible) {
        svg
          .append("line")
          .attr("x1", x(x1))
          .attr("y1", y(slope * x1 + intercept))
          .attr("x2", x(x2))
          .attr("y2", y(slope * x2 + intercept))
          .attr("stroke", "#f5f5f5")
          .attr("stroke-width", 4)
          .attr("stroke-dasharray", "5,5")
          .style("opacity", 0.8);
      }
    }
  }, [
    config,
    processedData,
    viewType,
    aggregate,
    onNodeSelect,
    onNodeHover,
    selectedId,
    chartSize,
  ]);

  return (
    <div className="chart-area">
      <div className="chart-area__plot">
        <div ref={chartRef} className="chart-tile"></div>
      </div>
    </div>
  );
}
